import {
  Dialog,
  DialogType,
  Stack,
  Icon,
  DefaultButton,
  DialogFooter,
  MessageBar,
  MessageBarType,
  Text,
  getTheme
} from "@fluentui/react";
import { useCallback, useState } from "react";

import { Card } from "../Card";
import { Loader } from "../Loader";
import { SignatureSelectorContent } from "../SignatureSelectorModal";
import {
  SignatureUpdateData,
  SignatureUpdateMethodResponse
} from "../SignatureSelectorModal/interfaces";

import {
  ActionsSection,
  ActionButtonsContainer,
  ActionButton,
  ActionDefaultButton
} from "./styles";

import { Account } from "@/components/Contracts/contract.interface";
import SignatureTable from "@/components/Contracts/DisplayContractInfo/ContractSignatures/SignatureTable";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { useSignatureResend } from "@/hooks/useSignatureResend";
import { useUpdateSignatureMethod } from "@/hooks/useUpdateSignatureMethod";
import {
  IDocumentEletronicSignature,
  ITermDataDraft
} from "@/modules/Terms/interfaces/ITerm.interface";

export interface ApiConfig {
  resendSignatureEndpoint: string;
  updateSignatureEndpoint: string;
}

interface ManageSignaturesModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  businessUnitId: number;
  apiConfig: ApiConfig;
  electronicSignature?: IDocumentEletronicSignature;
  account?: Account;
  termDataDraft?: ITermDataDraft;
  refetchData?: () => Promise<void>;
  canUpdateSignatureMethod?: boolean;
}

type ActiveSignatureAction = "UPDATE" | null;

export const ManageSignaturesModal = ({
  isOpen,
  onDismiss,
  electronicSignature,
  businessUnitId,
  account,
  apiConfig,
  refetchData,
  termDataDraft,
  canUpdateSignatureMethod = true
}: ManageSignaturesModalProps) => {
  const { execute } = useApi();
  const theme = getTheme();
  const [activeAction, setActiveAction] = useState<ActiveSignatureAction>(null);

  const handleResendAPI = useCallback(() => {
    return execute<void>({
      url: apiConfig.resendSignatureEndpoint,
      method: "POST",
      messages: {
        error: "Erro ao reenviar assinaturas",
        success: "Assinaturas reenviadas com sucesso"
      },
      showAlert: true
    });
  }, [apiConfig.resendSignatureEndpoint, execute]);

  const {
    canResendSignatureRequest,
    handleResend,
    isButtonDisabled: isResendButtonDisabled,
    isLoading: isResendLoading,
    isCooldownActive: isSignatureCooldownActive,
    startCooldown: startResendCooldown
  } = useSignatureResend({
    electronicSignature,
    onResend: handleResendAPI,
    cooldownKey: apiConfig.resendSignatureEndpoint
  });

  const handleUpdateSignatureMethodAPI = useCallback(
    (signatureData: SignatureUpdateData) => {
      return execute<SignatureUpdateMethodResponse>({
        url: apiConfig.updateSignatureEndpoint,
        method: "PATCH",
        bodyObj: signatureData,
        messages: {
          success: "Método de assinatura atualizado com sucesso",
          error: "Erro ao atualizar método de assinatura"
        },
        showAlert: true
      });
    },
    [apiConfig.updateSignatureEndpoint, execute]
  );

  const {
    handleUpdateSignatureMethod,
    isButtonDisabled: isUpdateButtonDisabled,
    isLoading: isUpdateLoading
  } = useUpdateSignatureMethod({
    onUpdate: handleUpdateSignatureMethodAPI,
    onUpdateSuccess: startResendCooldown,
    refetchData
  });

  const closeActionPanel = useCallback(() => setActiveAction(null), []);

  const handleConfirmSignatureMethod = useCallback(
    async (signatureData: SignatureUpdateData) => {
      return handleUpdateSignatureMethod(signatureData);
    },
    [handleUpdateSignatureMethod]
  );

  const handleResendSignatures = useCallback(() => {
    closeActionPanel();
    void handleResend();
  }, [closeActionPanel, handleResend]);

  const renderUpdateActionPanel = () => (
    <Stack
      as="section"
      styles={{
        root: {
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${theme.palette.neutralLight}`
        }
      }}
    >
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text
          as="h6"
          styles={{
            root: { margin: 0, fontSize: 16, fontWeight: 600 }
          }}
        >
          Alterar método de assinatura
        </Text>
      </Stack>

      <SignatureSelectorContent
        account={account}
        onConfirm={handleConfirmSignatureMethod}
        businessUnitId={businessUnitId}
        termDataDraft={termDataDraft}
        onCancel={closeActionPanel}
      />
    </Stack>
  );

  const renderActiveActionPanel = () => {
    if (activeAction === "UPDATE" && canUpdateSignatureMethod) {
      return renderUpdateActionPanel();
    }

    return null;
  };

  const handleDismiss = useCallback(() => {
    closeActionPanel();
    onDismiss();
  }, [closeActionPanel, onDismiss]);

  const isUpdateActionDisabled =
    !canResendSignatureRequest ||
    isResendLoading ||
    isUpdateButtonDisabled ||
    isSignatureCooldownActive;
  const isResendActionDisabled =
    isResendButtonDisabled || isUpdateButtonDisabled;

  const renderLoadingOverlay = () => {
    if (!isUpdateLoading) return null;

    return (
      <Loader customMessage="Aguarde enquanto processamos a alteração do método de assinatura..." />
    );
  };

  const pendingSignatureCount =
    electronicSignature?.document?.signers?.filter(
      signer => !signer.signature?.signed_at
    ).length ?? 0;

  const pendingSignatureLabel =
    pendingSignatureCount === 1
      ? "1 assinatura pendente"
      : `${pendingSignatureCount} assinaturas pendentes`;

  return (
    <>
      <Dialog
        hidden={!isOpen}
        onDismiss={handleDismiss}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: "Gerenciamento de assinaturas"
        }}
        modalProps={{
          styles: {
            main: {
              maxHeight: "90vh",
              overflow: "auto",
              "@media (max-width: 767px)": {
                minWidth: "95vw",
                maxWidth: "95vw"
              }
            }
          }
        }}
        minWidth={800}
        maxWidth={1000}
      >
        <div style={{ position: "relative" }}>
          <Stack tokens={{ childrenGap: 16 }}>
            <Card
              borderRadius="8px"
              padding="0.75rem 1rem"
              style={{
                backgroundColor: theme.palette.neutralLighterAlt,
                border: `1px solid ${theme.palette.neutralLight}`
              }}
            >
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
              >
                <Icon
                  iconName="Clock"
                  style={{ color: theme.palette.themePrimary, fontSize: 16 }}
                />

                <Text
                  styles={{
                    root: {
                      color: theme.palette.neutralPrimary,
                      fontSize: 14,
                      fontWeight: 600
                    }
                  }}
                >
                  {pendingSignatureLabel}
                </Text>
              </Stack>
            </Card>

            <Stack>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
              >
                <Icon
                  iconName="OpenEnrollment"
                  style={{ color: theme.palette.themePrimary, fontSize: 18 }}
                />

                <Text
                  variant="large"
                  styles={{ root: { fontSize: 16, fontWeight: 600 } }}
                >
                  Assinaturas do contrato
                </Text>
              </Stack>

              <Card
                borderRadius="8px"
                padding="1rem"
                style={{ marginTop: "8px" }}
              >
                <SignatureTable signatures={electronicSignature} />
              </Card>
            </Stack>

            <ActionsSection hidden={!canResendSignatureRequest}>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
                styles={{ root: { marginBottom: "12px" } }}
              >
                <Icon
                  iconName="Settings"
                  style={{
                    color: theme.palette.themePrimary,
                    fontSize: 16
                  }}
                />

                <Text
                  as="h6"
                  styles={{
                    root: { margin: 0, fontSize: 16, fontWeight: 600 }
                  }}
                >
                  Gerenciar
                </Text>
              </Stack>

              <ActionButtonsContainer horizontal>
                <ActionButton
                  onClick={handleResendSignatures}
                  disabled={isResendActionDisabled || activeAction === "UPDATE"}
                  text="Reenviar Assinaturas"
                />

                {canUpdateSignatureMethod && (
                  <ActionDefaultButton
                    onClick={() => setActiveAction("UPDATE")}
                    disabled={
                      isUpdateActionDisabled || activeAction === "UPDATE"
                    }
                    text="Alterar método de assinatura"
                  />
                )}
              </ActionButtonsContainer>

              {renderActiveActionPanel()}

              {isSignatureCooldownActive && (
                <MessageBar
                  messageBarType={MessageBarType.success}
                  styles={{ root: { marginTop: 10 } }}
                >
                  Assinaturas reenviadas com sucesso.
                </MessageBar>
              )}
            </ActionsSection>
          </Stack>

          {renderLoadingOverlay()}
        </div>

        <DialogFooter>
          <DefaultButton
            onClick={handleDismiss}
            text="Fechar"
            disabled={isUpdateLoading}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};
